import type { SystemStyleObject } from '@chakra-ui/react'

/**
 * Shared "prose content" styles for authored markdown — the ONE place that restores the
 * prose defaults Chakra v3's Preflight strips. Apply it identically to the Tiptap editor
 * (RichMarkdownEditor) AND every react-markdown display (PacketContent, PrintPacketCard,
 * NotesPanel, LegislatorNotesPanel, EmailDetailPage, reporting NotesBlockView, …) so a
 * document looks the same wherever it's authored or shown. (B6 fix set, 2026-08-28.)
 *
 * Why this is needed — Preflight resets:
 *  - heading `font-size` to inherit → h1/h2/h3 render at body size (all look identical);
 *  - `list-style: none` → bulleted/numbered lists show only an indent, no markers.
 * The per-site CSS previously restored only padding + weight, which is exactly why
 * blockquote/code survived (they were explicitly styled) and headings/lists didn't.
 *
 * Italics: Geist ships upright-only (no italic face), so `<em>` also needs
 * `font-synthesis: style` in index.css to slant. The `& em` rule here is the
 * belt-and-suspenders half; the font-synthesis change is the necessary other half.
 *
 * Scope carefully at call sites: these `& h1/h2/h3` / `& p` selectors are descendant
 * matches, so wrap ONLY the markdown region — not a container that also holds a Chakra
 * `<Heading>` (which renders an <h2> and would get resized).
 */
export const proseContentCss: SystemStyleObject = {
  '& h1': { fontSize: '2xl', fontWeight: 'bold', lineHeight: 1.25, mt: 3, mb: 1 },
  '& h2': { fontSize: 'xl', fontWeight: 'bold', lineHeight: 1.3, mt: 3, mb: 1 },
  '& h3': { fontSize: 'lg', fontWeight: 'semibold', lineHeight: 1.35, mt: 2, mb: 1 },
  '& p': { py: 1 },
  '& ul': { listStyleType: 'disc', listStylePosition: 'outside', pl: 5, py: 1 },
  '& ol': { listStyleType: 'decimal', listStylePosition: 'outside', pl: 5, py: 1 },
  '& li': { mb: 1 },
  '& em': { fontStyle: 'italic' },
  '& blockquote': { borderLeftWidth: '3px', borderLeftColor: 'border', pl: 3, color: 'ink.2' },
  '& a': { color: 'accent', textDecoration: 'underline' },
}
